/** Coalesce artifact bursts while fencing requests from a previous deck visit. */
export function createDeckRefresh<T>(options: {
  isCurrent: (deckId: string) => boolean;
  load: (deckId: string) => Promise<T>;
  apply: (value: T) => void;
  delay?: number;
}) {
  type Pending = { deckId: string; dirty: boolean; running: boolean; timer?: ReturnType<typeof setTimeout> };
  let pending: Pending | undefined;
  const cancel = () => {
    if (pending?.timer) clearTimeout(pending.timer);
    pending = undefined;
  };
  const schedule = (deckId: string) => {
    if (!options.isCurrent(deckId)) return;
    if (pending?.deckId !== deckId) {
      cancel();
      pending = { deckId, dirty: false, running: false };
    }
    const state = pending!;
    state.dirty = true;
    if (state.running || state.timer) return;
    const valid = () => pending === state && options.isCurrent(deckId);
    const run = async () => {
      state.timer = undefined;
      if (!valid()) return;
      state.running = true;
      state.dirty = false;
      try {
        const value = await options.load(deckId);
        if (valid()) options.apply(value);
      } catch {
        // The next event or reconnect retries a transient read failure.
      } finally {
        state.running = false;
        if (valid() && state.dirty) state.timer = setTimeout(run, options.delay ?? 250);
      }
    };
    state.timer = setTimeout(run, options.delay ?? 250);
  };
  return { schedule, cancel };
}
